import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Transactions"` — P11-07's second surface, laid out as the design draws it
 * (ADR 0018).
 *
 * **"Transactions" is the screen title and nothing else.** The table is `ledger_entry`, the
 * route is `/v1/billing/ledger`, the type is `LedgerEntryView` and the file is
 * `ledger-screen.tsx`. The glossary permits the word in customer-facing copy — which is where
 * it appears, in an `<h1>` — and bans it as an identifier, which is why nothing here is named
 * after it.
 *
 * **The running balance comes from the database, not from this screen.**
 * `ledger_entry.balance_after_minor` is written under `SELECT … FOR UPDATE` and is correct by
 * construction (design §7.9). Summing the rows in JavaScript to draw the column would produce
 * a second, weaker answer that disagrees with the first the moment a page boundary falls in
 * the wrong place.
 *
 * **What the design draws and this screen cannot do yet.** The date-range and kind filters are
 * drawn in their place but DISABLED: `GET /v1/billing/ledger` pages by cursor and takes no range
 * or kind, so a live control here could only filter the rows already loaded and call that the
 * range. Receipts are not built either, so they keep their place and are shown disabled. The four figures are
 * totals of the rows on this page, and their `title` says so.
 *
 * **Whose ledger it is (plan-014 D6; design §27.15.1; FR12-DOC-05).** The kicker names the balance and the workspaces
 * it funds (the prototype's "Shared balance €559.00 · funds Roma — main and Summer pop-up"), from the funding line
 * (`GET /v1/billing/funding`); sibling workspaces are named only for someone the server lets see them. "Export CSV"
 * exports this balance's ledger (one audited file). A member without the organization's finance over the balance — a
 * workspace Billing role — is told whose it is, and the page reads none of it (the API would refuse it too).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { AddCreditDialog } from "./add-credit-dialog.js";
import { fundsPhraseOf, useWorkspaceFunding } from "./funding-line.js";
import { apiProblemText } from "../../api/api-error.js";
import {
  formatSignedMoney,
  ledgerLabelOf,
  ledgerMethodOf,
  noMoney,
  sumMoney,
} from "./billing-copy.js";
import { ledgerGlyphOf } from "./ledger-glyph.js";
import { useCurrentWorkspace } from "../../shell/current-workspace.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { creditBalanceQuery, ledgerQuery, voiceAgentListQuery } from "../../api/query.js";
import { formatMoney } from "../../money.js";
import type { LedgerEntryView } from "../../api/gateway.js";

import "./billing.css";

const LEDGER_RANGE = ["7 days", "30 days", "This month", "90 days"] as const;
const LEDGER_KIND = ["All", "Usage", "Credit", "Invoice"] as const;
const filterSoon = () => portalText("finance", "m1206");

/** Broadsheet's `.seg`, drawn with its options disabled (the design's filter, not yet built). */
function LedgerFilter({
  name,
  legend,
  option,
  chosen,
}: {
  readonly name: string;
  readonly legend: string;
  readonly option: readonly string[];
  readonly chosen: string;
}): ReactNode {
  usePortalTranslation("finance");
  return (
    <div className="seg bl-seg-off" role="radiogroup" aria-label={legend} aria-disabled="true">
      {option.map((one) => (
        <label key={one} className="seg-opt">
          <input type="radio" name={name} value={one} checked={one === chosen} disabled readOnly />
          <span>{({ "7 days": portalText("finance", "m1197"), "30 days": portalText("finance", "m1256"), "This month": portalText("finance", "m0182"), "90 days": portalText("finance", "m1199"), "All": portalText("finance", "m0446"), "Usage": portalText("finance", "m1257"), "Credit": portalText("finance", "m0148"), "Invoice": portalText("finance", "m1145") } as Readonly<Record<string, string>>)[one]}</span>
        </label>
      ))}
    </div>
  );
}

export function LedgerScreen(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const [cursor, setCursor] = useState<string | null>(null);
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [outcome, setOutcome] = usePortalMessage();
  // plan-014 D6: the ledger is the balance's — read only by someone with the organization's finance over it.
  const { funding, failed: fundingFailed } = useWorkspaceFunding();
  const seesMoney = funding !== undefined && funding.financeAccess !== "none";
  const managesMoney = funding !== undefined && funding.financeAccess === "manage";
  const page = useQuery({ ...ledgerQuery(gateway, cursor), enabled: seesMoney });
  const credit = useQuery(creditBalanceQuery(gateway));
  const currentWorkspace = useCurrentWorkspace();
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const balanceName = funding?.displayName ?? (funding?.scope === "account" ? portalText("finance", "m0278") : portalText("finance", "m0279"));
  const exported = useMutation({
    mutationFn: () => gateway.exportLedgerEntries(funding?.billingAccountId ?? "", { from: null, to: null }),
    onSuccess: (file) => {
      setOutcome(() => (portalText("finance", "ledger.exported", { count: file.rowCount, balanceName })));
      window.location.assign(file.downloadUrl);
    },
    onError: (error) => setOutcome(() => (apiProblemText(error, portalText("finance", "m0281")))),
  });

  if (funding === undefined && !fundingFailed) {
    return <p className="c-note">{portalText("finance", "m0282")}</p>;
  }
  if (!seesMoney) {
    return (
      <section className="c-page bl-page bl-ledger-page">
        <Link to="/billing" className="bl-backlink">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("finance", "m0283")}</Link>
        <h1 className="bl-title">{portalText("finance", "m0284")}</h1>
        <p className="c-note" role="status">
          {portalText("finance", "m0285", { value1: funding === undefined ? portalText("finance", "m1143") : balanceName })}
        </p>
      </section>
    );
  }

  const row = page.data?.row ?? [];
  const added = row.filter((one) => one.amount.amountMinor > 0).map((one) => one.amount);
  const used = row
    .filter((one) => one.kind === "talk_charge" && one.amount.amountMinor < 0)
    .map((one) => one.amount);
  const reviewed = row
    .filter((one) => one.kind === "review_charge" && one.amount.amountMinor < 0)
    .map((one) => one.amount);
  const nextCursor = page.data?.nextCursor ?? null;
  const voiceAgentNameOf = (one: LedgerEntryView): string => {
    if (one.voiceAgentId === null) return "—";
    return (
      voiceAgentList.data?.find((each) => each.voiceAgentId === one.voiceAgentId)?.displayName ??
      "—"
    );
  };

  return (
    <section className="c-page bl-page bl-ledger-page">
      <Link to="/billing" className="bl-backlink">
        <Icon glyph={GLYPH.arrowLeft} size="sm" />{portalText("finance", "m0283")}</Link>

      <div className="bl-headline">
        <div>
          <p className="bl-headline-kicker">
            {funding === undefined
              ? portalText("finance", "m0286", { value1: currentWorkspace.workspace?.name ?? portalText("finance", "m0165"), value2: credit.data === undefined ? "…" : formatMoney(credit.data.available) })
              : `${balanceName} ${credit.data === undefined ? "…" : formatMoney(credit.data.available)} · ${fundsPhraseOf(funding)}`}
          </p>
          <h1 className="bl-title">{portalText("finance", "m0284")}</h1>
        </div>
        <div className="bl-actions">
          <Button
            variant="secondary"
            disabled={funding === undefined || exported.isPending}
            onClick={() => {
              exported.mutate();
            }}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            <span>{portalText("finance", "m0287")}</span>
          </Button>
          {managesMoney ? (
            <Button
              variant="primary"
              onClick={() => {
                setAddCreditOpen(true);
              }}
            >
              <Icon glyph={GLYPH.plus} size="md" />
              <span>{portalText("finance", "m0105")}</span>
            </Button>
          ) : null}
        </div>
      </div>

      {outcome === null ? null : (
        <p className="c-note" role="status">
          {outcome}
        </p>
      )}

      {/* The design's range and kind controls, in their place and disabled: the ledger route
          pages by cursor and takes neither, and filtering only the rows already loaded would
          call a page a range. */}
      <div className="bl-ledger-filters" title={filterSoon()}>
        <LedgerFilter name="ledgerRange" legend={portalText("finance", "m1144")} option={LEDGER_RANGE} chosen="30 days" />
        <LedgerFilter name="ledgerKind" legend={portalText("finance", "m0442")} option={LEDGER_KIND} chosen="All" />
      </div>

      <div className="bl-stats" title={portalText("finance", "m0288")}>
        <div className="bl-stat">
          <span className="bl-stat-value">
            {formatSignedMoney(added.length === 0 ? noMoney() : sumMoney(added))}
          </span>
          <span className="bl-stat-label">{portalText("finance", "m0289")}</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-value">
            {formatSignedMoney(used.length === 0 ? noMoney() : sumMoney(used))}
          </span>
          <span className="bl-stat-label">{portalText("finance", "m0290")}</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-value">
            {formatSignedMoney(reviewed.length === 0 ? noMoney() : sumMoney(reviewed))}
          </span>
          <span className="bl-stat-label">{portalText("finance", "m0291")}</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-value">
            {credit.data === undefined ? "…" : formatMoney(credit.data.available)}
          </span>
          <span className="bl-stat-label">{portalText("finance", "m0292")}</span>
        </div>
      </div>

      {row.length === 0 ? (
        <p className="c-note">{portalText("finance", "m0200")}</p>
      ) : (
        <div className="bl-scroll bl-ledger-table">
          <Table caption={portalText("finance", "m0293")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("finance", "m0294")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "m0295")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "m0296")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "m0297")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("finance", "m0110")}</TableHeaderCell>
                <TableHeaderCell numeric>{portalText("finance", "m0298")}</TableHeaderCell>
                <TableHeaderCell>
                  <span className="wt-visually-hidden">{portalText("finance", "m0299")}</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.map((one) => (
                <TableRow key={one.ledgerEntryId}>
                  <TableCell>{one.whenText}</TableCell>
                  <TableCell>
                    <span className="bl-with-glyph">
                      <Icon glyph={ledgerGlyphOf(one)} size="lg" tone="muted" />
                      {ledgerLabelOf(one)}
                    </span>
                  </TableCell>
                  <TableCell>{voiceAgentNameOf(one)}</TableCell>
                  <TableCell>{ledgerMethodOf(one)}</TableCell>
                  <TableCell numeric>{formatSignedMoney(one.amount)}</TableCell>
                  <TableCell numeric>{formatMoney(one.balanceAfter)}</TableCell>
                  <TableCell numeric>
                    <span className="bl-soon" title={portalText("finance", "m0230")}>{portalText("finance", "m0231")}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {cursor === null && nextCursor === null ? null : (
        <div className="bl-pager">
          {cursor === null ? null : (
            <Button
              variant="secondary"
              onClick={() => {
                setCursor(null);
              }}
            >{portalText("finance", "m0300")}</Button>
          )}
          {nextCursor === null ? null : (
            <Button
              variant="secondary"
              onClick={() => {
                setCursor(nextCursor);
              }}
            >{portalText("finance", "m0301")}</Button>
          )}
        </div>
      )}

      <p className="bl-ledger-foot">{portalText("finance", "m0302")}</p>

      <AddCreditDialog
        open={addCreditOpen}
        onClose={() => {
          setAddCreditOpen(false);
        }}
      />
    </section>
  );
}
